import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LegalFooter } from "@/components/LegalFooter";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 py-8 flex flex-col">
      <div className="mx-auto w-full max-w-4xl space-y-6 flex-1">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/">Back to Workshop</Link>
          </Button>
        </div>

        <section className="glass-panel rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Space101 operates this store and website, including all related information, content, features,
                tools, products and services, in order to provide you, the customer, with a curated shopping
                experience (the "Services"). Space101 is powered by Shopify, which enables us to provide the
                Services to you.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This Privacy Policy describes how we collect, use, and disclose your personal information when you
                visit, use, or make a purchase or other transaction using the Services or otherwise communicate with
                us. If there is a conflict between our Terms of Service and this Privacy Policy, this Privacy Policy
                controls with respect to the collection, processing, and disclosure of your personal information.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Please read this Privacy Policy carefully. By using and accessing any of the Services, you acknowledge
                that you have read this Privacy Policy and understand the collection, use, and disclosure of your
                information as described in this Privacy Policy.
              </p>
            </div>
          </div>
        </section>

        <section className="glass-panel rounded-2xl p-6 space-y-6">
          <div>
            <h2 className="text-base font-medium text-foreground">Personal Information We Collect or Process</h2>
            <p className="text-sm text-muted-foreground mt-2">
              When we use the term "personal information," we are referring to information that identifies or can
              reasonably be linked to you or another person. Personal information does not include information that is
              collected anonymously or that has been de-identified, so that it cannot identify or be reasonably linked
              to you.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              We may collect or process the following categories of personal information, including inferences drawn
              from this personal information, depending on how you interact with the Services, where you live, and as
              permitted or required by applicable law:
            </p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground mt-2 space-y-1">
              <li>Contact details including your name, address, billing address, shipping address, phone number, and email address.</li>
              <li>Financial information including payment card and transaction details and related payment confirmations.</li>
              <li>Account information including your username, password, security questions, preferences and settings.</li>
              <li>Transaction information including browsing, cart, wishlist, purchases, returns, exchanges, and cancellations.</li>
              <li>Communications with us, including customer support inquiries and related information.</li>
              <li>Device information including browser, network, IP address, and other unique identifiers.</li>
              <li>Usage information including how and when you interact with or navigate the Services.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Personal Information Sources</h2>
            <p className="text-sm text-muted-foreground mt-2">We may collect personal information from the following sources:</p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground mt-2 space-y-1">
              <li>Directly from you, including when you create an account, use the Services, or contact us.</li>
              <li>Automatically through the Services, including via cookies and similar technologies.</li>
              <li>From service providers that collect or process information on our behalf.</li>
              <li>From partners or other third parties.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">How We Use Your Personal Information</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Depending on how you interact with us or which Services you use, we may use personal information to:
            </p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground mt-2 space-y-1">
              <li>
                Provide, tailor, and improve the Services, including account management, payment processing, order
                fulfillment, returns/exchanges, preferences, reviews, and personalized shopping experiences.
              </li>
              <li>
                Support marketing and advertising communications and show relevant ads, including based on your activity
                on the Services and other websites.
              </li>
              <li>
                Support security and fraud prevention, including authentication and detection/investigation of illegal
                or malicious activity.
              </li>
              <li>Communicate with you for customer support and service-related updates.</li>
              <li>
                Comply with legal obligations, respond to lawful requests, enforce terms/policies, and defend rights.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">How We Disclose Personal Information</h2>
            <p className="text-sm text-muted-foreground mt-2">
              In certain circumstances, we may disclose your personal information to third parties for legitimate
              purposes subject to this Privacy Policy, including:
            </p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground mt-2 space-y-1">
              <li>With Shopify, vendors and service providers (for example payments, analytics, support, hosting, and shipping).</li>
              <li>With business and marketing partners for marketing and advertising in accordance with their own notices.</li>
              <li>When you direct us, request it, or otherwise consent to disclosure.</li>
              <li>With affiliates or within our corporate group.</li>
              <li>In connection with legal compliance, policy enforcement, or business transactions such as merger or bankruptcy.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Relationship with Shopify</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Services are hosted by Shopify, which collects and processes personal information about your access
              to and use of the Services in order to provide and improve the Services for you.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Information you submit to the Services will be transmitted to and shared with Shopify as well as third
              parties that may be located in countries other than where you reside, in order to provide and improve
              the Services for you.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              To learn more about how Shopify uses your personal information and any rights you may have, visit
              {" "}
              <a className="underline" href="https://privacy.shopify.com/en" target="_blank" rel="noreferrer">
                Shopify Consumer Privacy Policy
              </a>
              .
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Depending on where you live, you may exercise certain rights related to data processed by Shopify through
              the Shopify Privacy Portal.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Third Party Websites and Links</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Services may provide links to websites or online platforms operated by third parties. If you follow
              links to sites not affiliated with or controlled by us, review their privacy/security policies and terms.
              We are not responsible for those third-party properties.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Children's Data</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The Services are not intended for children, and we do not knowingly collect personal information from
              children under the age of majority in your jurisdiction. Parents/guardians may contact us to request
              deletion if a child has provided personal information.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Security and Retention of Your Information</h2>
            <p className="text-sm text-muted-foreground mt-2">
              No security measure is perfect, and we cannot guarantee perfect security. Retention depends on factors
              such as account/service needs, legal requirements, dispute resolution, and policy enforcement.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Your Rights and Choices</h2>
            <p className="text-sm text-muted-foreground mt-2">Depending on where you live, you may have rights including:</p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground mt-2 space-y-1">
              <li>Right to access/know personal information we hold.</li>
              <li>Right to delete personal information we maintain.</li>
              <li>Right to correct inaccurate personal information.</li>
              <li>Right of portability in certain circumstances.</li>
              <li>Right to opt out of sale/sharing for targeted advertising where applicable.</li>
              <li>Right to manage communication preferences, including unsubscribe options for marketing emails.</li>
              <li>For UK/EEA residents: rights to object/restrict processing and withdraw consent where applicable.</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              We may need to verify your identity before processing requests, and we may decline requests where
              permitted by law. You may designate an authorized agent where allowed.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Learn more about Shopify-specific rights at
              {" "}
              <a className="underline" href="https://privacy.shopify.com/en" target="_blank" rel="noreferrer">
                https://privacy.shopify.com/en
              </a>
              .
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Global Privacy Control info:
              {" "}
              <a className="underline" href="https://globalprivacycontrol.org/" target="_blank" rel="noreferrer">
                https://globalprivacycontrol.org/
              </a>
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Complaints</h2>
            <p className="text-sm text-muted-foreground mt-2">
              If you have complaints about how we process personal information, contact us using the details below.
              Depending on your location, you may have the right to appeal our decision or complain to your local data
              protection authority.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">International Transfers</h2>
            <p className="text-sm text-muted-foreground mt-2">
              We may transfer, store, and process personal information outside your country. Where required, we rely on
              recognized transfer mechanisms such as Standard Contractual Clauses.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Changes to This Privacy Policy</h2>
            <p className="text-sm text-muted-foreground mt-2">
              We may update this Privacy Policy from time to time for operational, legal, or regulatory reasons. We
              will post the revised policy and update relevant dates/notices as required by law.
            </p>
          </div>

          <div>
            <h2 className="text-base font-medium text-foreground">Contact</h2>
            <p className="text-sm text-muted-foreground mt-2">
              If you have any questions about our privacy practices or this Privacy Policy, or if you want to exercise
              your rights, contact us at:
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Email:
              {" "}
              <a className="underline" href="mailto:space101marketing@gmail.com">space101marketing@gmail.com</a>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Or create a ticket in The Workshop or Space101 Discord.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              For the purpose of applicable data protection laws, we are the data controller of your personal
              information.
            </p>
          </div>
        </section>
      </div>
      <LegalFooter />
    </div>
  );
}
